import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { API, ChannelDTO, CullDTO, Weekday } from '../../../../services/api/api';
import { SbLocalDateTime } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { ErrorService } from '../../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { ChartComponent, XYChartModel } from '@ngx-seatback/charts';
import { MsOptions } from '../../../../services/global/global-interfaces';
import { UtilService } from '../../../../services/util.service';
import { PermissionService } from '../../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

@Component({
  selector: 'sb-comp-channel-airtime-quota',
  templateUrl: './channel-airtime-quota.component.html',
  styleUrls: ['./channel-airtime-quota.component.scss'],
  standalone: false,
})
export class ChannelAirtimeQuotaComponent implements OnInit {
  @ViewChild('chart') chart: ChartComponent;

  debounceTimer: ReturnType<typeof setTimeout> = null;
  // tslint:disable-next-line:variable-name
  _cull: CullDTO;
  culls: CullDTO[];
  chartData: XYChartModel;
  channels: ChannelDTO[];
  channelIds: number[] = [];
  msCullOptions: MsOptions;

  constructor(
    private api: API,
    private errorHandler: ErrorService,
    public util: UtilService,
    public translate: TranslateService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.culls = [];
  }

  get cull(): CullDTO {
    return this._cull;
  }

  set cull(cull: CullDTO) {
    this._cull = cull;
    this.requestDebounced();
  }

  ngOnInit(): void {
    this.chartData = {
      title: 'Airtime Quota Overview',
      height: 350,
      width: 100,
      valueAssignments: [
        {
          valueName: 'Count',
          value: 'quota',
          categories: ['category'],
          color: '#000',
        },
      ],
      legend: false,
      labelBullet: true,
      useDefaultColors: true,
      stacked: false,
      data: [],
    };
    this.getBarChartData();
  }

  getBarChartData() {
    this.chartData.loadingDone = false;
    this.api
      .ss_nm_Channel_retrieveAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          if (data) {
            this.channels = data;
            for (const channel of data) {
              this.channelIds.push(channel.id);
            }
            this.retrieveChartData();
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  retrieveChartData() {
    const pTill = new SbLocalDateTime(DateTime.local().startOf('day').minus({ days: 1 }));
    const pFrom = new SbLocalDateTime(DateTime.local().startOf('day').minus({ days: 2 }));

    this.api
      .ss_nm_Reporting_airtimeQuotaGroupedBy('channel', {
        from: pFrom,
        till: pTill,
        productive: true,
        interval: 86400,
        weekday: [Weekday.MONDAY, Weekday.TUESDAY, Weekday.WEDNESDAY, Weekday.THURSDAY, Weekday.FRIDAY, Weekday.SATURDAY],
        hour: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
        playerCull: [this.cull ? this.cull.id : null],
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: data => {
          this.buildChartData(data);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  buildChartData(data) {
    const tempChartData = [];
    for (const channel of Object.keys(data)) {
      const d1 = data[channel];
      let quota = 0;
      let devices = 0;
      quota = d1.buckets[0].frames[0].data.quota;
      devices = d1.buckets[0].frames[0].data.devices;
      tempChartData.push({
        category: channel,
        quota: Math.round(quota * 100) + '%',
        pie: [
          {
            title: 'Devices',
            value: Math.round(devices),
          },
        ],
      });
    }
    this.chart.reloadData(tempChartData);
    this.chartData.loadingDone = true;
  }

  update($event, type: string) {
    if (type === 'PLAYER_CULLS') {
      if ($event && $event?.length > 0) {
        this.cull = $event[0];
      } else {
        this.cull = null;
      }
    }
  }

  msRequest(type: string, options?: MsOptions) {
    if (type === 'PLAYER_CULLS') {
      if (options) {
        this.msCullOptions = options;
      }
      if (this.msCullOptions?.pagination) {
        this.api
          .cs_Cull_retrieveAll(
            {
              search: this.util.encodeSearchQueryParam(false, this.msCullOptions.search),
              type: 'PlayerCull',
            },
            this.msCullOptions.pagination
          )
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (culls: CullDTO[]) => {
              this.culls = culls;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.getBarChartData();
    }, 500);
  }
}
